import { audio, type Voice } from './engine.js';
import { pickVoice } from './speech.js';

/**
 * What the countdown before a table's match sounds like.
 *
 * Chosen per device, not per table: it is a matter of taste, and nobody's
 * countdown is played on anybody else's phone. Each style has a sound for
 * each number and a different one for GO, so the start is unmistakable even
 * without looking.
 */
export type CountdownStyle =
  | 'voice' | 'announcer' | 'chipmunk' | 'robot'
  | 'beep' | 'rising' | 'falling' | 'race' | 'arcade' | 'clock' | 'drum' | 'boing'
  | 'none';

export interface CountdownStyleInfo {
  id: CountdownStyle;
  name: string;
  note: string;
  spoken?: boolean;
}

export const COUNTDOWN_STYLES: readonly CountdownStyleInfo[] = [
  { id: 'voice', name: 'Read out', note: '“Five, four, three, two, one, go!” in the voice below.', spoken: true },
  { id: 'announcer', name: 'Announcer', note: 'Slower and deeper, like a race start.', spoken: true },
  { id: 'chipmunk', name: 'Chipmunk', note: 'High and quick.', spoken: true },
  { id: 'robot', name: 'Robot', note: 'Flat and mechanical.', spoken: true },
  { id: 'beep', name: 'Steady beeps', note: 'The same beep each second, a long high one for go.' },
  { id: 'rising', name: 'Rising beeps', note: 'Each beep a step higher, building to go.' },
  { id: 'falling', name: 'Falling beeps', note: 'Each beep a step lower, then a bright go.' },
  { id: 'race', name: 'Starting lights', note: 'Low beeps, then one high beep: the motor-racing start.' },
  { id: 'arcade', name: 'Arcade', note: 'Eight-bit blips and a little fanfare.' },
  { id: 'clock', name: 'Clock', note: 'Tick, tock… and a bell.' },
  { id: 'drum', name: 'Drum', note: 'Drum beats getting louder, then a crash.' },
  { id: 'boing', name: 'Boing', note: 'Springs and a slide whistle. For the silly sound set.' },
  { id: 'none', name: 'Silent', note: 'Just the numbers on the screen.' },
];

export const DEFAULT_COUNTDOWN: CountdownStyle = 'rising';

const t = (type: OscillatorType, freq: number, dur: number, gain: number, extra: Partial<Voice> = {}): Voice =>
  ({ type, freq, to: extra.to ?? freq, dur, gain, ...extra });
const noise = (dur: number, gain: number, filter: number, delay = 0): Voice =>
  ({ type: 'noise', freq: 0, dur, gain, filter, delay });

/** Semitones above a base frequency. */
const st = (base: number, n: number): number => base * 2 ** (n / 12);

/** The sound for one step. `n` is the number shown (5…1), or 0 for GO. */
function voicesFor(style: CountdownStyle, n: number): Voice[] {
  const go = n === 0;
  const step = 5 - n;                    // 0 for the first number, 5 for go
  switch (style) {
    case 'beep':
      return go ? [t('sine', 1320, 0.7, 0.32, { hold: 0.45 })] : [t('sine', 880, 0.16, 0.3, { hold: 0.08 })];
    case 'rising':
      return go
        ? [t('triangle', st(523, 12), 0.8, 0.26, { hold: 0.5 }), t('sine', st(523, 19), 0.8, 0.14, { hold: 0.5 })]
        : [t('triangle', st(523, step * 2), 0.18, 0.3, { hold: 0.08 })];
    case 'falling':
      return go
        ? [t('triangle', st(523, 12), 0.8, 0.26, { hold: 0.5 }), t('sine', st(523, 16), 0.8, 0.14, { hold: 0.5 })]
        : [t('triangle', st(523, 10 - step * 2), 0.18, 0.3, { hold: 0.08 })];
    case 'race':
      return go
        ? [t('square', 880, 0.9, 0.14, { hold: 0.7, filter: 3000 })]
        : [t('square', 440, 0.22, 0.14, { hold: 0.14, filter: 2400 })];
    case 'arcade':
      return go
        ? [0, 4, 7, 12].map((k, i) => t('square', st(523, k), 0.16, 0.1, { delay: i * 0.08, filter: 5000 }))
          .concat([t('square', st(523, 12), 0.5, 0.1, { delay: 0.32, hold: 0.3, filter: 5000 })])
        : [t('square', st(392, step * 2), 0.09, 0.12, { filter: 5000 }), t('square', st(392, step * 2 + 7), 0.09, 0.1, { delay: 0.08, filter: 5000 })];
    case 'clock':
      return go
        ? [1568, 3136, 4700].map((f, i) => t('sine', f, 2.2, [0.28, 0.1, 0.05][i]!))
        : [noise(0.05, 0.5, n % 2 ? 3200 : 1800), t('sine', n % 2 ? 1800 : 1400, 0.04, 0.12)];
    case 'drum':
      return go
        ? [noise(1.6, 0.5, 9000), t('sine', 90, 0.6, 0.5, { to: 50 })]
        : [t('sine', 140, 0.25, 0.25 + step * 0.08, { to: 60 }), noise(0.12, 0.12 + step * 0.05, 1500)];
    case 'boing':
      return go
        ? [t('sine', 300, 0.7, 0.3, { to: 1400, wobble: { rate: 7, depth: 12 } })]
        : [t('sine', st(220, step * 3), 0.3, 0.3, { to: st(220, step * 3) * 2.2, wobble: { rate: 18, depth: 30 } })];
    default:
      return [];
  }
}

const WORDS = ['Go!', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

const SPOKEN: Record<string, { rate: number; pitch: number }> = {
  voice: { rate: 1.05, pitch: 1 },
  announcer: { rate: 0.85, pitch: 0.55 },
  chipmunk: { rate: 1.35, pitch: 2 },
  robot: { rate: 0.9, pitch: 0.05 },
};

/** The voices a player can pick from for the spoken styles: English first. */
export function countdownVoices(): SpeechSynthesisVoice[] {
  try {
    const all = speechSynthesis.getVoices();
    const en = all.filter((v) => /^en\b/i.test(v.lang));
    return en.length ? en : all;
  } catch {
    return [];
  }
}

function voiceNamed(uri: string): SpeechSynthesisVoice | null {
  if (uri) {
    const v = countdownVoices().find((x) => x.voiceURI === uri);
    if (v) return v;
  }
  return pickVoice();
}

/**
 * Sound one step of the countdown.
 * `n` is the number now showing, or 0 for GO.
 */
export function countdownStep(style: CountdownStyle, n: number, voiceUri = ''): void {
  const spoken = SPOKEN[style];
  if (spoken) {
    try {
      if (audio.settings.muted) return;
      speechSynthesis.cancel();                       // never let a slow voice fall behind
      const u = new SpeechSynthesisUtterance(WORDS[n] ?? String(n));
      const v = voiceNamed(voiceUri);
      if (v) u.voice = v;
      u.lang = v?.lang ?? 'en-GB';
      u.rate = spoken.rate;
      u.pitch = spoken.pitch;
      u.volume = Math.max(0.1, audio.settings.volume);
      speechSynthesis.speak(u);
    } catch { /* no speech here: the numbers on screen still count */ }
    return;
  }
  audio.playVoices(voicesFor(style, n));
}

/** Play a whole countdown, for choosing one in Settings. Returns a stop function. */
export function previewCountdown(style: CountdownStyle, voiceUri = '', from = 5): () => void {
  audio.unlock();
  const timers: number[] = [];
  for (let i = 0; i <= from; i++) {
    timers.push(window.setTimeout(() => countdownStep(style, from - i, voiceUri), i * 1000));
  }
  return () => {
    for (const id of timers) clearTimeout(id);
    try { speechSynthesis.cancel(); } catch { /* ignore */ }
  };
}
